import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button as Btn } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";

import { useTranslation } from "contexts/TranslationContext";
import { useGetVendorsQuery } from "services/auth";
import env from "shared/constants/env";
import routes, { appRoute } from "shared/constants/routes";

// Category tab icons — reuse the existing assets (no new UI).
import educationSelected from "assets/svg/educationSelected.svg";
import educationUnSelected from "assets/svg/educationUnSelected.svg";
import healthcareSelected from "assets/svg/healthcareSelected.svg";
import healthcareUnSelected from "assets/svg/healthcareUnSelected.svg";
import furnitureSelected from "assets/svg/furnitureSelected.svg";
import furnitureUnSelected from "assets/svg/furnitureUnSelected.svg";
import electronicsSelected from "assets/svg/electronicsSelected.svg";
import electronicsUnSelected from "assets/svg/electronicsUnSelected.svg";
import emptyProducts from "assets/svg/emptyProducts.svg";
import vendorPlaceholder from "assets/svg/vendorPlaceholder.png";

const CATEGORY_KEYS = {
  EDUCATION: "education",
  HEALTHCARE: "healthcare",
  FURNITURE: "furniture",
  ELECTRONICS: "electronics",
};

// ── Backend filter args per tab ──────────────────────────────────────────────
// Server-side filter by subproductName + productCode. EDU/HC each have their
// own dedicated query. Furniture and Electronics share the OTHER subproduct
// (Murabaha product) and are then split client-side by CAT_NAME_EN.
//
// Page-size = 6 because the tab displays at most 6; pagination.totalCount in
// the response tells us whether to show the "View All" button.
const TAB_LIMIT = 6;

const QUERY_ARGS = {
  EDU: {
    subproductName: "EDUCATIONFEES",
    productCode: "SMB.TA.PERSONAL.FINANCE.EDU",
    limit: TAB_LIMIT,
  },
  HC: {
    subproductName: "HEALTHCARE",
    productCode: "SMB.TA.PERSONAL.FINANCE.EDU",
    limit: TAB_LIMIT,
  },
  OTHER: {
    subproductName: "OTHER",
    productCode: "SMB.TA.MURABAHA.FINANCE",
    // OTHER covers Furniture + Electronics + Tourism + …; pull a wider page so
    // the client-side split by CAT_NAME_EN doesn't run out of items per tab.
    limit: 20,
  },
};

// Client-side split inside the OTHER bucket (Furniture vs Electronics).
const OTHER_TAB_PREDICATE = {
  [CATEGORY_KEYS.FURNITURE]: (v) =>
    (v?.CAT_NAME_EN || "").toLowerCase() === "furniture",
  [CATEGORY_KEYS.ELECTRONICS]: (v) =>
    (v?.CAT_NAME_EN || "").toLowerCase() === "electronics",
};

const FeaturedProductsV2 = ({ data }) => {
  const { translate, language } = useTranslation();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(CATEGORY_KEYS.EDUCATION);
  const isArabic = language?.code === "ar";

  // Vendor ids whose logo failed to load. Tracked in state (rather than mutating
  // img.src in onError) so the fallback survives re-renders and can only be
  // applied once per vendor — otherwise a broken image retriggers onError on
  // every render and floods the image endpoint with requests.
  const [failedLogos, setFailedLogos] = useState({});
  const markLogoFailed = (id) =>
    // Returning prev unchanged when already marked stops the update — without
    // this, a placeholder that also fails would loop indefinitely.
    setFailedLogos((prev) => (prev[id] ? prev : { ...prev, [id]: true }));

  // ── Per-tab queries with `skip` on inactive tabs ─────────────────────────
  // RTK Query caches each tab's response independently; switching tabs re-uses
  // the prior fetch instead of refetching.
  const eduQuery = useGetVendorsQuery(QUERY_ARGS.EDU, {
    skip: activeCategory !== CATEGORY_KEYS.EDUCATION,
  });
  const hcQuery = useGetVendorsQuery(QUERY_ARGS.HC, {
    skip: activeCategory !== CATEGORY_KEYS.HEALTHCARE,
  });
  const otherQuery = useGetVendorsQuery(QUERY_ARGS.OTHER, {
    skip:
      activeCategory !== CATEGORY_KEYS.FURNITURE &&
      activeCategory !== CATEGORY_KEYS.ELECTRONICS,
  });

  // Map active tab → its query result + the rendered vendor list + totalCount.
  // totalCount is taken directly from server for EDU/HC; for Furniture/
  // Electronics we count the client-side split because the server-side total
  // covers all OTHER vendors (both tabs together).
  const tabState = useMemo(() => {
    if (activeCategory === CATEGORY_KEYS.EDUCATION) {
      return {
        isLoading: eduQuery.isLoading,
        isError: eduQuery.isError,
        vendors: eduQuery.data?.data || [],
        totalCount: eduQuery.data?.pagination?.totalCount ?? 0,
      };
    }
    if (activeCategory === CATEGORY_KEYS.HEALTHCARE) {
      return {
        isLoading: hcQuery.isLoading,
        isError: hcQuery.isError,
        vendors: hcQuery.data?.data || [],
        totalCount: hcQuery.data?.pagination?.totalCount ?? 0,
      };
    }
    // Furniture / Electronics share the OTHER bucket — split by CAT_NAME_EN.
    const allOther = otherQuery.data?.data || [];
    const split = allOther.filter(OTHER_TAB_PREDICATE[activeCategory] || (() => false));
    return {
      isLoading: otherQuery.isLoading,
      isError: otherQuery.isError,
      vendors: split,
      totalCount: split.length, // server total covers both Furn+Elec; use the split count
    };
  }, [
    activeCategory,
    eduQuery.isLoading, eduQuery.isError, eduQuery.data,
    hcQuery.isLoading, hcQuery.isError, hcQuery.data,
    otherQuery.isLoading, otherQuery.isError, otherQuery.data,
  ]);

  const categories = [
    {
      key: CATEGORY_KEYS.EDUCATION,
      label: translate("HOME.EDUCATIONAL_PRODUCTS_TITLE"),
      iconSelected: educationSelected,
      iconUnselected: educationUnSelected,
      viewAllRoute: routes.SCHOOLS_V2 + "/list",
    },
    {
      key: CATEGORY_KEYS.HEALTHCARE,
      label: translate("HOME.HEALTHCARE_TITLE"),
      iconSelected: healthcareSelected,
      iconUnselected: healthcareUnSelected,
      viewAllRoute: routes.DENTAL_V2 + "/list",
    },
    {
      key: CATEGORY_KEYS.FURNITURE,
      // Same label fallback as legacy FeaturedProducts.
      label: translate("HOME.FURNITURE_PRODUCTS_TITLE" || "Furniture"),
      iconSelected: furnitureSelected,
      iconUnselected: furnitureUnSelected,
      viewAllRoute: null, // no V1 listing for the OTHER subproduct
    },
    {
      key: CATEGORY_KEYS.ELECTRONICS,
      label: translate("HOME.ELECTRONICS_PRODUCTS_TITLE" || "Electronics"),
      iconSelected: electronicsSelected,
      iconUnselected: electronicsUnSelected,
      viewAllRoute: null,
    },
  ];

  const activeConfig =
    categories.find((c) => c.key === activeCategory) || categories[0];

  // Localized accessors with fallback to the other language.
  const vendorName = (v) =>
    (isArabic ? v?.VENDOR_NAME_AR : v?.VENDOR_NAME_EN) ||
    v?.VENDOR_NAME_EN ||
    v?.VENDOR_NAME_AR ||
    "-";
  const vendorDesc = (v) =>
    (isArabic ? v?.SHORT_DESC_AR : v?.SHORT_DESC_EN) ||
    v?.SHORT_DESC_EN ||
    v?.SHORT_DESC_AR ||
    "";

  // Apply gate: pre-app status check, then route by vendor's SUBPRODUCT_NAME.
  // Furniture/Electronics (OTHER) fall through to a "Coming soon" toast in V1.
  const handleApplyNow = async (vendor) => {
    if (data?.length > 50) {
      toast.error(translate("APPLICATION_ALREADY_EXISTS"));
      return;
    }

    try {
      const preAppStatus = await fetch(
        `${env.API_ENDPOINT_V2}app/pre-app-status?id=${
          JSON.parse(localStorage.getItem("@@TAMWEEL_USER"))?.IDB_USR_ID
        }`
      );
      const response = await preAppStatus.json();

      if (
        response?.result?.completedSteps.length >= 4 &&
        response?.result?.status === "COMPLETED"
      ) {
        if (vendor?.SUBPRODUCT_NAME === "EDUCATIONFEES") {
          navigate(appRoute(routes.SCHOOLS_V2 + `/${vendor.ID}`));
        } else if (vendor?.SUBPRODUCT_NAME === "HEALTHCARE") {
          navigate(appRoute(routes.DENTAL_V2 + `/${vendor.ID}`));
        } else {
          // SUBPRODUCT_NAME === "OTHER" — no V1 product page yet.
          toast.info(translate("VOUCHERS.COMING_SOON") || "Coming soon");
        }
      } else {
        navigate(appRoute(routes.PRE_APP) + `?version=2`);
        toast.error("Please complete pre-application steps and try again.");
      }
    } catch (error) {
      console.error("Pre-App Status error:", error);
      toast.error(
        "Something went wrong in checking pre app status. Please try again."
      );
    }
  };

  const handleViewAll = () => {
    if (!activeConfig?.viewAllRoute) return;
    navigate(appRoute(activeConfig.viewAllRoute));
  };

  const { isLoading, isError, vendors, totalCount } = tabState;
  const hasVendors = vendors.length > 0;
  // Show "View All" when there are more matching vendors than fit in this tab
  // AND the category actually has a listing page (EDU/HC only in V1).
  const showViewAll = !!activeConfig.viewAllRoute && totalCount > TAB_LIMIT;

  return (
    <div className="rounded-xl mb-4 px-4 py-6 bg-white">
      {/* Category tabs — same visual treatment as legacy FeaturedProducts */}
      <div className="flex flex-wrap justify-center gap-6 md:gap-10 mb-10 md:mb-14">
        {categories.map((category) => {
          const isActive = category.key === activeCategory;
          return (
            <button
              key={category.key}
              type="button"
              onClick={() => setActiveCategory(category.key)}
              className="flex flex-col items-center gap-3"
            >
              <div
                className={`flex items-center justify-center rounded-full w-14 h-14 md:w-20 md:h-20 shadow-md transition-colors ${
                  isActive ? "bg-primary" : "bg-white"
                }`}
              >
                <img
                  src={isActive ? category.iconSelected : category.iconUnselected}
                  alt={category.label}
                  className="w-7 h-7 md:w-9 md:h-9"
                />
              </div>
              <span className="text-[#101010] text-xs md:text-base font-normal">
                {category.label}
              </span>
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingOutlined style={{ fontSize: 32, color: "#1F225B" }} />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <img src={emptyProducts} alt="Error" className="w-40 md:w-56" />
          <p className="text-[#5F5F5F] text-sm md:text-base text-center">
            {translate("SIGN_IN.SOMETHING_WENT_WRONG") ||
              "Something went wrong. Please try again."}
          </p>
        </div>
      ) : hasVendors ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-8">
          {vendors.slice(0, TAB_LIMIT).map((vendor) => (
            <div
              key={vendor.ID}
              className="bg-white rounded-lg border border-[#E0E0E0] cursor-pointer max-w-[27rem]"
              onClick={() => handleApplyNow(vendor)}
            >
              <div className="relative mx-2 mt-2 mb-0 h-40 md:h-48 rounded-lg overflow-hidden bg-[#F6F6F6] flex items-center justify-center">
                <img
                  src={
                    // OCI PAR signed URLs expire after ~1h — fall back once.
                    !vendor.LOGO_URL || failedLogos[vendor.ID]
                      ? vendorPlaceholder
                      : vendor.LOGO_URL
                  }
                  alt={vendorName(vendor)}
                  className="relative z-10 p-4 w-[67%] max-h-full object-contain"
                  onError={() => markLogoFailed(vendor.ID)}
                />
              </div>
              <div className="p-4 md:p-6">
                <h3 className="text-[#1F1F1F] text-base md:text-lg font-semibold mb-1 md:mb-2">
                  {vendorName(vendor)}
                </h3>
                <p className="text-[#8E9196] text-xs md:text-sm">
                  {vendorDesc(vendor)}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <img src={emptyProducts} alt="No products" className="w-40 md:w-56" />
          <p className="text-[#5F5F5F] text-sm md:text-base text-center">
            {translate("HOME.NO_FEATURED_PRODUCTS") ||
              "Currently, there are no products under this category"}
          </p>
        </div>
      )}

      <div className="flex justify-center mt-20">
        {showViewAll && (
          <Btn
            className="rounded-full bg-primary text-white font-semibold p-5 w-36 border border-primary hover:!border-primary hover:border-solid hover:!text-primary"
            onClick={handleViewAll}
            disabled={!activeConfig.viewAllRoute}
          >
            {translate("HOME.VIEW_ALL") || "View All"}
          </Btn>
        )}
      </div>
    </div>
  );
};

export default FeaturedProductsV2;
